import type { SfxName } from "./stage";

/**
 * EduPoly's sounds – all made with the Web Audio API, no files. The audio context is only created after the first tap / key press (browsers
 * demand that), and the sound can be switched off; the choice is remembered. In a class the sound starts off.
 */
const KEY = "edu-mono-snd";

export function soundPref(inClass: boolean): boolean {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch { /* storage may be blocked */ }
  return !inClass;
}
export function saveSoundPref(on: boolean) {
  try { localStorage.setItem(KEY, on ? "1" : "0"); } catch { /* ignore */ }
}

type AudioGlobals = { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };

export class MonoSound {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private lastStep = 0;
  on: boolean;

  constructor(on: boolean) { this.on = on; }

  /** call from a user gesture (tap, key) */
  unlock() {
    if (this.ctx) { if (this.ctx.state === "suspended") void this.ctx.resume().catch(() => undefined); return; }
    try {
      const g = globalThis as unknown as AudioGlobals;
      const C = g.AudioContext ?? g.webkitAudioContext;
      if (!C) return;
      const ctx = new C();
      const master = ctx.createGain(); master.gain.value = 0.55;
      const comp = ctx.createDynamicsCompressor(); comp.threshold.value = -14; comp.ratio.value = 4;
      master.connect(comp); comp.connect(ctx.destination);
      const len = Math.floor(ctx.sampleRate * 0.6), buf = ctx.createBuffer(1, len, ctx.sampleRate), d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
      this.ctx = ctx; this.master = master; this.noise = buf;
    } catch { this.ctx = null; }
  }
  setOn(on: boolean) { this.on = on; if (on) this.unlock(); }
  dispose() { try { void this.ctx?.close(); } catch { /* ignore */ } this.ctx = null; }

  private tone(freq: number, t0: number, dur: number, o: { type?: OscillatorType; gain?: number; to?: number; attack?: number } = {}) {
    const c = this.ctx!, g = c.createGain(), osc = c.createOscillator();
    osc.type = o.type ?? "sine";
    osc.frequency.setValueAtTime(freq, t0);
    if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t0 + dur);
    const peak = o.gain ?? 0.3, att = o.attack ?? 0.006;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + att);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g); g.connect(this.master!);
    osc.start(t0); osc.stop(t0 + dur + 0.05);
  }
  private hiss(t0: number, dur: number, o: { f0: number; f1?: number; q?: number; gain?: number; type?: BiquadFilterType } = { f0: 1000 }) {
    const c = this.ctx!, src = c.createBufferSource(), f = c.createBiquadFilter(), g = c.createGain();
    src.buffer = this.noise!; src.loop = true;
    f.type = o.type ?? "bandpass"; f.Q.value = o.q ?? 1.2;
    f.frequency.setValueAtTime(o.f0, t0);
    if (o.f1) f.frequency.exponentialRampToValueAtTime(o.f1, t0 + dur);
    const peak = o.gain ?? 0.25;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + Math.min(0.02, dur / 3));
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(f); f.connect(g); g.connect(this.master!);
    src.start(t0, Math.random() * 0.3); src.stop(t0 + dur + 0.05);
  }

  play(name: SfxName) {
    if (!this.on) return;
    if (!this.ctx) return;                         // not unlocked yet – stay silent rather than throw
    const c = this.ctx;
    if (c.state === "suspended") { void c.resume().catch(() => undefined); }
    const t = c.currentTime + 0.01;
    switch (name) {
      case "dice": {
        for (let i = 0; i < 9; i++) this.hiss(t + i * 0.07 + Math.random() * 0.02, 0.05, { f0: 1500 + Math.random() * 2500, q: 3, gain: 0.16 });
        for (let i = 0; i < 4; i++) this.tone(180 + Math.random() * 60, t + 0.08 + i * 0.16, 0.06, { type: "triangle", gain: 0.12, to: 90 });
        this.tone(220, t + 0.72, 0.08, { type: "triangle", gain: 0.22, to: 110 });
        this.tone(260, t + 0.8, 0.08, { type: "triangle", gain: 0.18, to: 130 });
        break;
      }
      case "step": {
        const now = c.currentTime;
        if (now - this.lastStep < 0.09) return;
        this.lastStep = now;
        this.tone(420 + Math.random() * 40, t, 0.07, { type: "sine", gain: 0.2, to: 210 });
        this.hiss(t, 0.03, { f0: 1800, q: 1, gain: 0.05 });
        break;
      }
      case "coin": {
        this.tone(1318, t, 0.14, { type: "square", gain: 0.07 });
        this.tone(1760, t + 0.07, 0.3, { type: "square", gain: 0.07 });
        this.tone(2637, t + 0.07, 0.2, { type: "sine", gain: 0.07 });
        break;
      }
      case "pay": {
        this.tone(660, t, 0.12, { type: "triangle", gain: 0.2 });
        this.tone(494, t + 0.1, 0.22, { type: "triangle", gain: 0.2 });
        this.tone(330, t + 0.22, 0.3, { type: "triangle", gain: 0.16 });
        break;
      }
      case "build": {
        for (let i = 0; i < 2; i++) {
          this.tone(150, t + i * 0.16, 0.1, { type: "sine", gain: 0.35, to: 70 });
          this.hiss(t + i * 0.16, 0.05, { f0: 900, q: 0.8, gain: 0.14 });
        }
        this.tone(1046, t + 0.34, 0.3, { type: "triangle", gain: 0.12 });
        break;
      }
      case "buy": {
        [523, 659, 784, 1046].forEach((f, i) => this.tone(f, t + i * 0.07, 0.3, { type: "triangle", gain: 0.2 }));
        this.tone(1568, t + 0.3, 0.45, { type: "sine", gain: 0.1 });
        break;
      }
      case "card": {
        this.hiss(t, 0.28, { f0: 500, f1: 2600, q: 0.9, gain: 0.2 });
        this.tone(880, t + 0.3, 0.1, { type: "triangle", gain: 0.12 });
        this.tone(1175, t + 0.38, 0.22, { type: "triangle", gain: 0.12 });
        break;
      }
      case "jail": {
        for (let i = 0; i < 2; i++) {
          this.tone(147, t + i * 0.22, 0.5, { type: "square", gain: 0.1, to: 140 });
          this.tone(233, t + i * 0.22, 0.45, { type: "square", gain: 0.08, to: 225 });
          this.hiss(t + i * 0.22, 0.12, { f0: 3500, q: 5, gain: 0.12 });
        }
        break;
      }
      case "whoosh": {
        this.hiss(t, 0.4, { f0: 250, f1: 2400, q: 0.9, gain: 0.22 });
        break;
      }
      case "cheer": {
        [523, 659, 784, 1046, 1318].forEach((f, i) => { this.tone(f, t + i * 0.1, 0.5, { type: "triangle", gain: 0.2 }); this.tone(f * 2, t + i * 0.1, 0.3, { type: "sine", gain: 0.05 }); });
        this.hiss(t + 0.1, 1.1, { f0: 5200, q: 0.6, gain: 0.09, type: "highpass" });
        break;
      }
      case "pop": {
        this.tone(520, t, 0.12, { type: "sine", gain: 0.25, to: 980 });
        break;
      }
      case "click": {
        this.tone(960, t, 0.04, { type: "sine", gain: 0.12 });
        break;
      }
    }
  }
}
